import type { JSX } from "react";
import { Button } from "../../../components";

// [S-509h] The review action bar — ONE definition, shared by every clause card.
// Order is fixed (Approve · Keep · Type · Skip) and a card can never reorder or
// relabel a button. The one thing that varies is Approve: it only appears when
// there is actually something to apply — i.e. the clause carries a certified
// recommendation. An ADDITIONAL clause (no counterpart, e.g. a flagged
// merchandising addition) has nothing to "approve", so showing Approve there is
// a dead button — you can only Keep it, Type an instruction, or Skip. See
// `canApply` below. The agent supplies clause DATA; it never decides the buttons.
//
//   Apply change → apply the recommendation to the draft (only when one exists).
//                 Key `approve`; the label was "Approve" until 2026-10-15, which
//                 Bill Jacks read as "approve what I already have" — Keep does that.
//   Keep    → leave the clause as-is                (deterministic /review/decide)
//   Type    → hand control back to the conversation, scoped to this clause
//   Skip    → decide this clause later              (deterministic /review/decide)
//
// Approve/Keep/Skip map to the backend decision verbs; Type is a client-only
// affordance (focus the composer) — the review + chat are one state (spec §3a),
// so "type your own" is just continuing the conversation about the open clause.
type ActionKey = "approve" | "keep" | "type" | "skip";

const REVIEW_ACTIONS: ReadonlyArray<{
  key: ActionKey;
  label: string;
  className: string;
  title: string;
}> = [
  {
    key: "approve",
    label: "Apply change",
    className: "sa-rev__btn sa-rev__btn--apply",
    title: "Accept the recommendation and apply it to the draft",
  },
  {
    key: "keep",
    label: "Keep",
    className: "sa-rev__btn sa-rev__btn--keep",
    title: "Leave the clause as it stands",
  },
  {
    key: "type",
    label: "Type",
    className: "sa-rev__btn sa-rev__btn--type",
    title: "Tell the agent what to do in your own words",
  },
  {
    key: "skip",
    label: "Decide later",
    className: "sa-rev__btn sa-rev__btn--skip",
    title: "Decide this clause later",
  },
];

export function ReviewActions({
  index,
  pending,
  onDecide,
  onType,
  canApply = true,
  applied = false,
  decisionNote,
}: {
  index: number;
  pending: boolean;
  onDecide: (index: number, decision: "apply" | "keep" | "skip" | "undo") => void;
  onType: () => void;
  // [review-quality] Whether this clause has a certified recommendation to apply.
  // False for additional clauses (no counterpart) — Approve is omitted so there's
  // no dead "apply nothing" button.
  canApply?: boolean;
  // [review-undo] This clause was already APPROVED (its recommendation applied to
  // the draft). An approved clause can't be re-approved/kept/skipped — reopening it
  // offers only Undo, in place of the whole action bar, which reverts the change and
  // returns the clause to a fresh, actionable state.
  applied?: boolean;
  // [review-honest] The last decision did not land in the draft — an Undo that
  // could not revert, or an Apply change that was held back: say why, so the
  // button doesn't look like it did nothing (or claim what didn't happen).
  decisionNote?: string | undefined;
}): JSX.Element {
  const run = (key: ActionKey): void => {
    if (key === "approve") onDecide(index, "apply");
    else if (key === "keep") onDecide(index, "keep");
    else if (key === "skip") onDecide(index, "skip");
    else onType();
  };
  if (applied) {
    return (
      <div className="sa-rev__acts" data-testid="review-actions">
        <Button
          variant="bare"
          className="sa-rev__btn sa-rev__btn--undo"
          title="Undo this change and restore the clause's original text"
          disabled={pending}
          onClick={() => onDecide(index, "undo")}
          data-testid="review-action-undo"
        >
          Undo
        </Button>
        {decisionNote !== undefined && decisionNote.length > 0 && (
          <span className="sa-rev__decision-note" role="status" data-testid="review-decision-note">
            {decisionNote}
          </span>
        )}
      </div>
    );
  }
  const actions = canApply
    ? REVIEW_ACTIONS
    : REVIEW_ACTIONS.filter((a) => a.key !== "approve");
  return (
    <div className="sa-rev__acts" data-testid="review-actions">
      {actions.map((a) => (
        <Button
          key={a.key}
          variant="bare"
          className={a.className}
          title={a.title}
          disabled={pending}
          onClick={() => run(a.key)}
          data-testid={`review-action-${a.key}`}
        >
          {a.label}
        </Button>
      ))}
      {decisionNote !== undefined && decisionNote.length > 0 && (
        <span className="sa-rev__decision-note" role="status" data-testid="review-decision-note">
          {decisionNote}
        </span>
      )}
    </div>
  );
}
